//////////////////////////////////////////////////////////////////////////////
//
//  푸시 캠페인 추가/수정 팝업
//
//  작성자: MediInStep Team
//  생성일: 2025-12-29
//
//  설명: 푸시 캠페인 등록 및 수정을 위한 팝업 다이얼로그
//
//////////////////////////////////////////////////////////////////////////////

import { Component } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Grid from "@mui/material/Grid";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import { request } from "app/network";
import fireAlert from "utils/sweetAlert";
import { MaterialUI } from "context";
import UserSearchSelector from "./UserSearchSelector";


interface AddCampaignDialogProps {
    open: boolean;
    editingCampaign?: any | null;
    onClose: () => void;
    onSuccess: () => void;
    darkMode?: boolean;
}

interface AddCampaignDialogState {
    name: string;
    description: string;
    title: string;
    body: string;
    imageUrl: string;
    data: string;
    topic: string;
    topicOptions: Array<{ code: string; codeName: string }>;  // FCM Topic options from common codes
    targetAll: boolean;
    targetFilter: string;
    selectedUserIds: number[];
    scheduledDt: Date | null;
    sendMode: "immediate" | "scheduled";
    loading: boolean;
    darkMode: boolean;
    tenantSeqId: number | null;
    seqId?: number | null;
}

class AddCampaignDialogClass extends Component<AddCampaignDialogProps, AddCampaignDialogState> {
    static contextType = MaterialUI;

    constructor(props: AddCampaignDialogProps) {
        super(props);
        const initialDarkMode = props.darkMode ?? false;

        this.state = {
            name: "",
            description: "",
            title: "",
            body: "",
            imageUrl: "",
            data: "",
            topic: "",
            topicOptions: [],
            targetAll: true,
            targetFilter: "",
            selectedUserIds: [],
            scheduledDt: null,
            sendMode: "immediate",
            loading: false,
            darkMode: initialDarkMode,
            tenantSeqId: null,
            seqId: null,
        };
    }

    async componentDidMount() {
        const SELECTED_TENANT_KEY = "mediinstep_selected_tenant";
        const savedTenantId = localStorage.getItem(SELECTED_TENANT_KEY);

        if (savedTenantId) {
            this.setState({ tenantSeqId: parseInt(savedTenantId, 10) });
        }

        // Load FCM Topic options from common codes
        try {
            const response = await request("CommonCode.getByGroup", {
                pathParams: { groupCode: "FCM_TOPIC" },
            });
            const topics = response.data.map((item: any) => ({
                code: item.code,
                codeName: item.codeName,
            }));
            this.setState({ topicOptions: topics });
        } catch (error) {
            console.error("Failed to load FCM topics:", error);
        }
    }

    componentDidUpdate(prevProps: AddCampaignDialogProps) {
        const context = this.context as any;
        const currentDarkMode = this.props.darkMode ?? context?.darkMode ?? false;

        if (currentDarkMode !== this.state.darkMode) {
            this.setState({ darkMode: currentDarkMode });
        }

        if (this.props.open && !prevProps.open) {
            const SELECTED_TENANT_KEY = "mediinstep_selected_tenant";
            const savedTenantId = localStorage.getItem(SELECTED_TENANT_KEY);
            const tenantSeqId = savedTenantId ? parseInt(savedTenantId, 10) : this.state.tenantSeqId;

            if (this.props.editingCampaign) {
                // 수정 모드
                const campaign = this.props.editingCampaign;
                const scheduledDt = campaign.scheduledDt ? new Date(campaign.scheduledDt) : null;
                const sendMode = scheduledDt ? "scheduled" : "immediate";

                this.setState({
                    seqId: campaign.seqId,
                    name: campaign.name || "",
                    description: campaign.description || "",
                    title: campaign.title || "",
                    body: campaign.body || "",
                    imageUrl: campaign.imageUrl || "",
                    data: campaign.data || "",
                    topic: campaign.topic || "",
                    targetAll: campaign.targetAll ?? true,
                    targetFilter: campaign.targetFilter || "",
                    selectedUserIds: [],
                    scheduledDt,
                    sendMode,
                    loading: false,
                    darkMode: currentDarkMode,
                    tenantSeqId,
                });
            } else {
                // 신규 등록 모드
                this.setState({
                    seqId: null,
                    name: "",
                    description: "",
                    title: "",
                    body: "",
                    imageUrl: "",
                    data: "",
                    topic: "",
                    targetAll: true,
                    targetFilter: "",
                    selectedUserIds: [],
                    scheduledDt: null,
                    sendMode: "immediate",
                    loading: false,
                    darkMode: currentDarkMode,
                    tenantSeqId,
                });
            }
        }
    }

    handleSubmit = async () => {
        const { name, title, body, topic, tenantSeqId, seqId, sendMode, scheduledDt } = this.state;

        // 필수 필드 검증
        if (!name.trim()) {
            fireAlert({
                icon: "warning",
                title: "캠페인 이름 필요",
                text: "캠페인 이름을 입력해주세요.",
            }, this.state.darkMode);
            return;
        }

        if (!title.trim()) {
            fireAlert({
                icon: "warning",
                title: "알림 제목 필요",
                text: "알림 제목을 입력해주세요.",
            }, this.state.darkMode);
            return;
        }

        if (!body.trim()) {
            fireAlert({
                icon: "warning",
                title: "알림 내용 필요",
                text: "알림 내용을 입력해주세요.",
            }, this.state.darkMode);
            return;
        }

        // if (!topic.trim()) {
        //     fireAlert({
        //         icon: "warning",
        //         title: "Topic 필요",
        //         text: "FCM Topic을 입력해주세요.",
        //     }, this.state.darkMode);
        //     return;
        // }

        if (sendMode === "scheduled" && !scheduledDt) {
            fireAlert({
                icon: "warning",
                title: "예약 시간 필요",
                text: "예약 발송 시간을 선택해주세요.",
            }, this.state.darkMode);
            return;
        }

        if (sendMode === "scheduled" && scheduledDt && scheduledDt <= new Date()) {
            fireAlert({
                icon: "warning",
                title: "잘못된 예약 시간",
                text: "예약 시간은 현재 시간 이후여야 합니다.",
            }, this.state.darkMode);
            return;
        }

        // 특정 사용자 타겟팅 시 최소 1명 이상 선택 확인
        if (!this.state.targetAll && (!this.state.selectedUserIds || this.state.selectedUserIds.length === 0)) {
            fireAlert({
                icon: "warning",
                title: "사용자 선택 필요",
                text: "발송 대상 사용자를 최소 1명 이상 선택해주세요.",
            }, this.state.darkMode);
            return;
        }

        if (!tenantSeqId) {
            fireAlert({
                icon: "error",
                title: "테넌트 정보 없음",
                text: "테넌트 정보를 찾을 수 없습니다. 다시 로그인해주세요.",
            }, this.state.darkMode);
            return;
        }

        try {
            this.setState({ loading: true });

            const campaignData = {
                name: this.state.name,
                description: this.state.description,
                title: this.state.title,
                body: this.state.body,
                imageUrl: this.state.imageUrl || null,
                data: this.state.data || null,
                topic: this.state.topic || null, // Allow null if empty
                targetAll: this.state.targetAll,
                targetFilter: this.state.targetFilter || null,
                selectedUserIds: !this.state.targetAll && this.state.selectedUserIds.length > 0
                    ? this.state.selectedUserIds.filter((id): id is number => id !== null && id !== undefined)
                    : null,
                scheduledDt: sendMode === "scheduled" && scheduledDt ? scheduledDt.toISOString() : null,
                tenantSeqId,
                status: "PENDING",
            };

            console.log("=== Campaign Data ===");
            console.log("targetAll:", this.state.targetAll);
            console.log("selectedUserIds (state):", this.state.selectedUserIds);
            console.log("selectedUserIds (filtered):", campaignData.selectedUserIds);
            console.log("Full campaign data:", JSON.stringify(campaignData, null, 2));

            if (seqId) {
                // 수정
                await request("PushCampaign.update", {
                    pathParams: { id: seqId.toString() },
                    paramz: campaignData,
                });

                fireAlert({
                    icon: "success",
                    title: "캠페인 수정 완료",
                    text: "캠페인이 성공적으로 수정되었습니다.",
                    timer: 1500,
                    showConfirmButton: false,
                }, this.state.darkMode);
            } else {
                // 신규 등록
                await request("PushCampaign.create", {
                    paramz: campaignData,
                });

                fireAlert({
                    icon: "success",
                    title: "캠페인 등록 완료",
                    text: "캠페인이 성공적으로 등록되었습니다.",
                    timer: 1500,
                    showConfirmButton: false,
                }, this.state.darkMode);
            }

            this.setState({ loading: false });
            this.props.onSuccess();
            this.props.onClose();
        } catch (error: any) {
            console.error("캠페인 저장 실패:", error);
            this.setState({ loading: false });

            fireAlert({
                icon: "error",
                title: "저장 실패",
                text: error?.response?.data?.message || "캠페인 저장에 실패했습니다.",
            }, this.state.darkMode);
        }
    };

    render() {
        const { open, onClose, editingCampaign } = this.props;
        const { name, description, title, body, imageUrl, data, topic, targetAll, targetFilter, sendMode, scheduledDt, loading, darkMode } = this.state;

        return (
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="md"
                fullWidth
                disableEnforceFocus
                PaperProps={{
                    sx: {
                        backgroundColor: darkMode ? "#1a2035" : "#ffffff",
                        color: darkMode ? "#ffffff" : "#000000",
                    },
                }}
            >
                <DialogTitle>
                    <div style={{ fontSize: '1.5rem', fontWeight: 500 }}>
                        {editingCampaign ? "캠페인 수정" : "캠페인 생성"}
                    </div>
                </DialogTitle>
                <DialogContent>
                    <MDBox pt={2}>
                        <Grid container spacing={3}>
                            {/* 캠페인 기본 정보 */}
                            <Grid item xs={12}>
                                <MDTypography variant="h6" fontWeight="medium" mb={2}>
                                    캠페인 정보
                                </MDTypography>
                            </Grid>
                            <Grid item xs={12}>
                                <MDInput
                                    label="캠페인 이름 *"
                                    fullWidth
                                    value={name}
                                    onChange={(e: any) => this.setState({ name: e.target.value })}
                                    placeholder="예: 신년 프로모션 캠페인"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <MDInput
                                    label="캠페인 설명"
                                    fullWidth
                                    multiline
                                    rows={2}
                                    value={description}
                                    onChange={(e: any) => this.setState({ description: e.target.value })}
                                    placeholder="캠페인에 대한 간단한 설명"
                                />
                            </Grid>

                            {/* 알림 내용 */}
                            <Grid item xs={12}>
                                <MDTypography variant="h6" fontWeight="medium" mb={2} mt={2}>
                                    알림 내용
                                </MDTypography>
                            </Grid>
                            <Grid item xs={12}>
                                <MDInput
                                    label="알림 제목 *"
                                    fullWidth
                                    value={title}
                                    onChange={(e: any) => this.setState({ title: e.target.value })}
                                    placeholder="사용자에게 표시될 알림 제목"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <MDInput
                                    label="알림 내용 *"
                                    fullWidth
                                    multiline
                                    rows={3}
                                    value={body}
                                    onChange={(e: any) => this.setState({ body: e.target.value })}
                                    placeholder="사용자에게 표시될 알림 내용"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <MDInput
                                    label="이미지 URL"
                                    fullWidth
                                    value={imageUrl}
                                    onChange={(e: any) => this.setState({ imageUrl: e.target.value })}
                                    placeholder="https://example.com/image.jpg"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <MDInput
                                    label="추가 데이터 (JSON)"
                                    fullWidth
                                    multiline
                                    rows={2}
                                    value={data}
                                    onChange={(e: any) => this.setState({ data: e.target.value })}
                                    placeholder='{"key": "value"}'
                                />
                            </Grid>

                            {/* 타겟팅 설정 */}
                            <Grid item xs={12}>
                                <MDTypography variant="h6" fontWeight="medium" mb={2} mt={2}>
                                    타겟팅 설정
                                </MDTypography>
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel
                                        id="fcm-topic-label"
                                        sx={{
                                            color: darkMode ? "#a0aec0" : "rgba(0, 0, 0, 0.6)",
                                            "&.Mui-focused": {
                                                color: darkMode ? "#63b3ed" : "#1976d2",
                                            },
                                        }}
                                    >
                                        FCM Topic
                                    </InputLabel>
                                    <Select
                                        labelId="fcm-topic-label"
                                        value={topic}
                                        label="FCM Topic"
                                        size="medium"
                                        onChange={(e) => this.setState({ topic: e.target.value })}
                                        sx={{
                                            height: "45px",
                                            backgroundColor: darkMode ? "#1a202c" : "#fff",
                                            color: darkMode ? "#fff" : "#000",
                                            "& .MuiOutlinedInput-notchedOutline": {
                                                borderColor: darkMode ? "#4a5568" : "rgba(0, 0, 0, 0.23)",
                                            },
                                            "&:hover .MuiOutlinedInput-notchedOutline": {
                                                borderColor: darkMode ? "#718096" : "rgba(0, 0, 0, 0.87)",
                                            },
                                            "& .MuiSelect-select": {
                                                paddingTop: "10.5px",
                                                paddingBottom: "10.5px",
                                            },
                                        }}
                                    >
                                        <MenuItem value="">
                                            <em>선택 안함 (개별 발송)</em>
                                        </MenuItem>
                                        {this.state.topicOptions.map((option: { code: string; codeName: string }) => (
                                            <MenuItem key={option.code} value={option.code}>
                                                {option.codeName} ({option.code})
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            {/* 모든 사용자에게 발송 - Topic이 선택되지 않았을 때만 표시 */}
                            {!topic && (
                                <>
                                    <Grid item xs={12}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={targetAll}
                                                    onChange={(e) => this.setState({ targetAll: e.target.checked, selectedUserIds: [] })}
                                                />
                                            }
                                            label="모든 사용자에게 발송"
                                        />
                                    </Grid>
                                    {!targetAll && (
                                        <Grid item xs={12}>
                                            <UserSearchSelector
                                                selectedUserIds={this.state.selectedUserIds}
                                                tenantSeqId={this.state.tenantSeqId}
                                                darkMode={this.state.darkMode}
                                                onSelectionChange={(userIds: number[]) => this.setState({ selectedUserIds: userIds })}
                                            />
                                        </Grid>
                                    )}
                                </>
                            )}

                            {/* 발송 방식 */}
                            <Grid item xs={12}>
                                <MDTypography variant="h6" fontWeight="medium" mb={2} mt={2}>
                                    발송 방식
                                </MDTypography>
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl component="fieldset">
                                    <RadioGroup
                                        value={this.state.sendMode}
                                        onChange={(e) => this.setState({ sendMode: e.target.value as "immediate" | "scheduled" })}
                                    >
                                        <FormControlLabel
                                            value="immediate"
                                            control={<Radio />}
                                            label="즉시 발송 (임시저장 후 수동 발송)"
                                        />
                                        <FormControlLabel
                                            value="scheduled"
                                            control={<Radio />}
                                            label="예약 발송"
                                        />
                                    </RadioGroup>
                                </FormControl>
                            </Grid>
                            {this.state.sendMode === "scheduled" && (
                                <Grid item xs={12}>
                                    <TextField
                                        label="발송 예정 시간"
                                        type="datetime-local"
                                        fullWidth
                                        value={this.state.scheduledDt ? new Date(this.state.scheduledDt.getTime() - this.state.scheduledDt.getTimezoneOffset() * 60000).toISOString().slice(0, 16) : ""}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            if (value) {
                                                this.setState({ scheduledDt: new Date(value) });
                                            } else {
                                                this.setState({ scheduledDt: null });
                                            }
                                        }}
                                        InputLabelProps={{
                                            shrink: true,
                                            sx: {
                                                color: this.props.darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
                                                '&.Mui-focused': {
                                                    color: this.props.darkMode ? 'rgba(255, 255, 255, 0.87)' : '#1976d2',
                                                },
                                            },
                                        }}
                                        helperText="예약된 시간에 자동으로 발송됩니다"
                                        inputProps={{
                                            min: new Date(new Date().getTime() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16)
                                        }}
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                backgroundColor: this.props.darkMode ? 'rgba(255, 255, 255, 0.05)' : '#fff',
                                                color: this.props.darkMode ? 'rgba(255, 255, 255, 0.87)' : 'rgba(0, 0, 0, 0.87)',
                                            },
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: this.props.darkMode ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
                                            },
                                            '& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: this.props.darkMode ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.87)',
                                            },
                                            '& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: this.props.darkMode ? 'rgba(255, 255, 255, 0.87)' : '#1976d2',
                                            },
                                            '& .MuiFormHelperText-root': {
                                                color: this.props.darkMode ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
                                            },
                                            '& input[type="datetime-local"]::-webkit-calendar-picker-indicator': {
                                                filter: this.props.darkMode ? 'invert(1)' : 'none',
                                                cursor: 'pointer',
                                            },
                                        }}
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </MDBox>
                </DialogContent>
                <DialogActions>
                    <MDButton onClick={onClose} color="secondary" disabled={loading}>
                        취소
                    </MDButton>
                    <MDButton onClick={this.handleSubmit} variant="gradient" color="info" disabled={loading}>
                        {loading ? "저장 중..." : editingCampaign ? "수정" : "생성"}
                    </MDButton>
                </DialogActions>
            </Dialog>
        );
    }
}

function AddCampaignDialog(props: AddCampaignDialogProps) {
    return <AddCampaignDialogClass {...props} />;
}

export default AddCampaignDialog;
